// Browser auth client for the hosted accounts layer (PRD 012 increment 1).
//
// Deliberately hand-rolled (no better-auth/react dependency): the browser only
// needs four stable better-auth HTTP endpoints, and the repo's ethos is a single
// static bundle with minimal deps (and the root pins zod 3, which better-auth's
// client would fight). The server (worker/src/better-auth.ts) is the real
// better-auth; this is just a typed `fetch` façade + a reactive session store.
//
// The auth origin is the bridge Worker (mcp.claudepad.io in prod). Cookies are
// credentialed and cross-origin: every call sends `credentials: 'include'`, and
// the Worker echoes the origin + `access-control-allow-credentials`. In dev the
// Vite proxy forwards /api/auth → the local `wrangler dev`, so it's same-origin.

import { useSyncExternalStore } from 'react'
import { BRIDGE_API_ORIGIN } from '../bridge/config'

export interface AuthUser {
  id: string
  email: string
  name: string
  image?: string | null
  emailVerified?: boolean
}

export type AuthStatus = 'loading' | 'authed' | 'anon'

interface AuthState {
  status: AuthStatus
  user: AuthUser | null
}

const AUTH_BASE = BRIDGE_API_ORIGIN // '' in dev → same-origin via the Vite proxy

function authUrl(path: string): string {
  return `${AUTH_BASE}/api/auth${path}`
}

// --- reactive store ---------------------------------------------------------

let state: AuthState = { status: 'loading', user: null }
const listeners = new Set<() => void>()

function setState(next: AuthState): void {
  state = next
  for (const l of listeners) l()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Where to land back in the SPA after an OAuth / magic-link round-trip. */
function callbackURL(): string {
  return `${window.location.origin}/#/account`
}

/** Fetch the current session and update the store. Safe to call repeatedly. */
export async function refreshSession(): Promise<AuthUser | null> {
  try {
    const res = await fetch(authUrl('/get-session'), {
      credentials: 'include',
      headers: { 'content-type': 'application/json' }
    })
    const data = res.ok ? ((await res.json()) as { user?: AuthUser } | null) : null
    const user = data?.user ?? null
    setState({ status: user ? 'authed' : 'anon', user })
    return user
  } catch {
    // Network/CORS failure — treat as signed out rather than spin forever.
    setState({ status: 'anon', user: null })
    return null
  }
}

let primed = false
/** Lazily prime the session on first hook use (one request per page load). */
function ensurePrimed(): void {
  if (primed) return
  primed = true
  void refreshSession()
}

/** Reactive auth state for components. */
export function useAuth(): AuthState {
  ensurePrimed()
  return useSyncExternalStore(subscribe, () => state)
}

// --- actions ----------------------------------------------------------------

/** Start an OAuth sign-in: ask the Worker for the provider URL, then navigate. */
export async function signInSocial(provider: 'google' | 'github'): Promise<void> {
  const res = await fetch(authUrl('/sign-in/social'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      provider,
      callbackURL: callbackURL(),
      errorCallbackURL: callbackURL()
    })
  })
  if (!res.ok) throw new Error(`sign-in failed (${res.status})`)
  const data = (await res.json()) as { url?: string }
  if (data.url) window.location.href = data.url
}

/** Send a magic-link email. Resolves once the email is queued (no session yet). */
export async function signInMagicLink(email: string): Promise<void> {
  const res = await fetch(authUrl('/sign-in/magic-link'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, callbackURL: callbackURL() })
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(msg || `magic-link request failed (${res.status})`)
  }
}

/** Sign out and clear the local session state. */
export async function signOut(): Promise<void> {
  try {
    await fetch(authUrl('/sign-out'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' }
    })
  } finally {
    setState({ status: 'anon', user: null })
  }
}
